import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Loader2, CalendarOff, X, Clock3, CheckCircle2, XCircle, Ban } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { easerGet, easerPost, easerDelete, errorMessage, fmtShortDate, toISODate } from '../../lib/easerApi';

const STATUS = {
  pending: { label: 'En attente', icon: Clock3, className: 'bg-[hsl(var(--accent))] text-white' },
  approved: { label: 'Acceptée', icon: CheckCircle2, className: 'bg-[hsl(var(--brand-sage))] text-white' },
  refused: { label: 'Refusée', icon: XCircle, className: 'bg-destructive text-destructive-foreground' },
  cancelled: { label: 'Annulée', icon: Ban, className: 'bg-muted text-muted-foreground' },
};

export function LeaveStatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return <Badge className={`${s.className} hover:${s.className} gap-1`} data-testid="leave-status"><s.icon className="h-3 w-3" /> {s.label}</Badge>;
}

export function EaserLeaves({ onUnauthorized }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ start: '', end: '', type: 'conges_payes', reason: '' });

  const load = useCallback(async () => {
    try {
      const res = await easerGet('/api/easer/leaves');
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err, 'Impossible de charger les congés'));
      setData({ items: [], types: {} });
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const openForm = () => {
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    setForm({ start: toISODate(tomorrow), end: toISODate(tomorrow), type: 'conges_payes', reason: '' });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.start || !form.end) return toast.error('Indiquez les dates');
    setSaving(true);
    try {
      await easerPost('/api/easer/leaves', form);
      toast.success("Demande envoyée à l'agence");
      setOpen(false);
      await load();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (leave) => {
    if (!window.confirm('Annuler cette demande ?')) return;
    try {
      await easerDelete(`/api/easer/leaves/${leave.id}`);
      toast.success('Demande annulée');
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  if (!data) return <div className="space-y-3"><Skeleton className="h-12 rounded-xl" /><Skeleton className="h-28 rounded-xl" /></div>;

  return (
    <div className="space-y-4" data-testid="easer-leaves">
      <Button onClick={openForm} size="lg" className="w-full h-12 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="leave-new">
        <Plus className="h-4 w-4 mr-2" /> Nouvelle demande de congés
      </Button>

      {data.items.length === 0 ? (
        <Card className="p-8 text-center" data-testid="leaves-empty">
          <CalendarOff className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">Aucune demande</p>
          <p className="text-sm text-muted-foreground mt-1">Vos demandes et leur statut apparaîtront ici.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.items.map((l) => (
            <Card key={l.id} className="p-4 border-border shadow-sm" data-testid="leave-item">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold">{l.type_label}</p>
                  <p className="text-sm text-muted-foreground">Du {fmtShortDate(l.start)} au {fmtShortDate(l.end)}</p>
                  {l.reason && <p className="text-sm text-muted-foreground mt-1 italic">« {l.reason} »</p>}
                  {l.decision_note && <p className="text-sm mt-1"><span className="text-muted-foreground">Réponse de l'agence :</span> {l.decision_note}</p>}
                </div>
                <LeaveStatusBadge status={l.status} />
              </div>
              {l.status === 'pending' && (
                <Button variant="ghost" size="sm" className="mt-2 text-muted-foreground" onClick={() => cancel(l)} data-testid="leave-cancel"><X className="h-4 w-4 mr-1" /> Annuler la demande</Button>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { if (!saving) setOpen(o); }}>
        <DialogContent className="max-w-md w-[95vw] rounded-2xl" data-testid="leave-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk']">Demande de congés</DialogTitle>
            <DialogDescription>L'agence reçoit votre demande par e-mail et vous répond ici.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="leave-start">Du</Label>
                <Input id="leave-start" type="date" value={form.start} min={toISODate(new Date())} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value, end: f.end < e.target.value ? e.target.value : f.end }))} className="h-11" data-testid="leave-start" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="leave-end">Au</Label>
                <Input id="leave-end" type="date" value={form.end} min={form.start} onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} className="h-11" data-testid="leave-end" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className="h-11" data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(data.types || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="leave-reason">Motif (optionnel)</Label>
              <Textarea id="leave-reason" rows={2} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} data-testid="leave-reason" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="leave-submit">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
